import { useState, useEffect } from 'react';
import { categories, getCategoryIcon } from '@/mocks/categories';
import { articleLinks } from '@/mocks/articles';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const topArticles = articleLinks.slice(0, 6);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <nav className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 max-w-[1280px] mx-auto">
          <a
            href="/"
            className="flex items-center gap-2 group"
          >
            <h1 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 tracking-tight">
              <span className="text-primary-600">Kakobuy</span> Finds
            </h1>
          </a>

          <div className="hidden md:flex items-center gap-6">
            <a href="/" className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap">
              Home
            </a>
            <a href="#categories" className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap">
              Categories
            </a>
            <a href="#guide" className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap">
              Guides
            </a>
            <a href="#faq" className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap">
              FAQ
            </a>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-semibold rounded-md bg-secondary-100 text-secondary-800 hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              kakobuy sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              buy it now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-background-50 z-40 overflow-y-auto">
          <div className="px-4 py-5 space-y-6">
            <div className="flex items-center justify-between pb-3 border-b border-background-200/60">
              <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Navigation</span>
              <button
                onClick={() => setMobileOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 cursor-pointer"
                aria-label="Close menu"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <a href="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-background-100 text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer">
                <i className="ri-home-line text-accent-500"></i>
                Home
              </a>
              <a href="#categories" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-background-100 text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer">
                <i className="ri-grid-line text-accent-500"></i>
                Categories
              </a>
              <a href="#guide" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-background-100 text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer">
                <i className="ri-book-open-line text-accent-500"></i>
                Guides
              </a>
              <a href="#faq" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-background-100 text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer">
                <i className="ri-questionnaire-line text-accent-500"></i>
                FAQ
              </a>
            </div>

            <div>
              <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3 block">Product Categories</span>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-background-100 border border-background-200/40 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:bg-primary-50/50 transition-all cursor-pointer"
                  >
                    <span className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 shrink-0">
                      <i className={`ri-${getCategoryIcon(cat.slug)} text-xs`}></i>
                    </span>
                    <span className="truncate">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <div>
              <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3 block">Popular Articles</span>
              <div className="space-y-2">
                {topArticles.map((article) => (
                  <a
                    key={article.slug}
                    href={`/${article.slug}/`}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-start gap-3 px-3 py-2.5 rounded-lg bg-background-100 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    <span className="inline-block px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[10px] font-semibold shrink-0 mt-0.5">
                      {article.category}
                    </span>
                    <span className="leading-snug line-clamp-2">{article.title}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3 pt-4 border-t border-background-200/60">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex-1 px-4 py-3 text-sm font-semibold rounded-lg bg-secondary-100 text-secondary-800 text-center hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                kakobuy sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex-1 px-5 py-3 text-sm font-semibold rounded-lg bg-primary-500 text-background-50 text-center hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                buy it now
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}